import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiX,
  HiEye,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi';
import config from '../../config';
import { fetchUserList } from '../../services/userService';
import UserModelView from './modelview';
import { useSearchParams } from 'react-router-dom';

const usersCache = {
  userData: null,
  page: 1,
  totalRecords: 0,
  firstLoad: true,
};

const buildAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = config.baseApi || '';
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const formatRegisteredAt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';

  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const day = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${day} ${month}, ${year} ${hours}:${minutes} ${ampm}`;
};

const normalizeUserItem = (item, index) => {
  const imageRaw = item.image || item.profileImage || item.profile_image || item.avatar || item.photo;
  // const imageUrl = buildAbsoluteUrl(imageRaw);

  return {
    ...item,
    id: item.id || item._id || item.userId || item.uuid || item.key || `user-${index}`,
    name: item.name || item.fullName || item.full_name || item.username || 'N/A',
    email: item.email || item.userEmail || item.user || '',
    phone: item.phone || item.phoneNumber || item.phone_number || item.mobile || '',
    image: item.image || null,
    createdAt: item.createdAt || item.created_at || null,
    status: item.status || item.isActive !== undefined ? (item.isActive || item.is_active) : true,
    isActive: item.isActive !== undefined ? item.isActive : (item.is_active !== undefined ? item.is_active : true),
  };
};


const Users = () => {
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(usersCache.page || 1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState(usersCache.userData || []);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(usersCache.totalRecords || 0);
  const [error, setError] = useState(null);

  const itemsPerPage = 10;

  // Get date filters from URL
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  
  // Guard flags to prevent duplicate API calls
  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false);
  const searchDebounceTimerRef = useRef(null);
  const lastCallParamsRef = useRef({ page: null, search: null });

  const getPaginationRange = () => {
    if (totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Debounce search query
  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage((currentPage) => currentPage !== 1 ? 1 : currentPage);
    }, 500);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Fetch user data from API
  const loadUserData = async () => {
    const currentParams = { page: currentPage, search: debouncedSearch, startDate, endDate };
    const lastParams = lastCallParamsRef.current;

    const isSameParams = lastParams.page === currentParams.page && 
                        lastParams.search === currentParams.search &&
                        lastParams.startDate === currentParams.startDate &&
                        lastParams.endDate === currentParams.endDate;
    
    if (isSameParams) {
      if (isInitialMountRef.current) {
        if (lastParams.page !== null) {
          return;
        }
      } else {
        return;
      }
    }

    if (isLoadingRef.current) {
      return;
    }

    setIsLoading(true);

    lastCallParamsRef.current = currentParams;
    isLoadingRef.current = true;
    setError(null);
    try {
      const encodedSearch = encodeURIComponent(debouncedSearch || '');

const response = await fetchUserList(
  currentPage,
  itemsPerPage,
  encodedSearch,
  startDate,
  endDate
);

     
      setIsLoading(false);

      const extractItems = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res?.data?.docs)) return res.data.docs;
        if (Array.isArray(res?.data?.list)) return res.data.list;
        if (Array.isArray(res?.data?.users)) return res.data.users;
        if (Array.isArray(res?.result)) return res.result;
        if (Array.isArray(res?.list)) return res.list;
        if (Array.isArray(res?.users)) return res.users;
        return [];
      };

      const items = extractItems(response);
      const normalizedItems = items.map((item, idx) =>
        normalizeUserItem(item, idx)
      );
      
      // Extract total from various possible response structures
      const total =
        response?.total ??
        response?.data?.total ??
        response?.data?.totalDocs ??
        response?.data?.count ??
        response?.data?.pagination?.total ??
        response?.pagination?.total ??
        response?.data?.pagination?.totalRecords ??
        response?.data?.pagination?.totalCount ??
        response?.data?.data?.total ??
        response?.data?.data?.pagination?.total ??
        response?.result?.total ??
        response?.result?.pagination?.total ??
        null;
      
      // Calculate total: use API total if available, otherwise estimate based on items
      let computedTotal = total;
      if (computedTotal === null || computedTotal === undefined) {
        // Total not found in API response - estimate based on items
        if (normalizedItems.length === itemsPerPage) {
          // Full page suggests more pages might exist - estimate conservatively
          computedTotal = currentPage * itemsPerPage + 1;
        } else if (normalizedItems.length > 0) {
          // Less than full page means we're on the last page
          computedTotal = (currentPage - 1) * itemsPerPage + normalizedItems.length;
        } else {
          computedTotal = 0;
        }
      }
      // If total is explicitly 0 from API, keep it as 0 (no records)

      // The API handles pagination server-side, so we use the items as-is
      const paginatedItems = normalizedItems;

      const successFlag =
        response?.success === true ||
        response?.status === true ||
        response?.code === 200 ||
        response?.statusCode === 200 ||
        /success/i.test(response?.message || '') ||
        items.length > 0 ||
        Array.isArray(items);

      if (successFlag) {
        setUserData(paginatedItems);
        setTotalRecords(computedTotal);
        if (paginatedItems.length === 0) {
          setError(null); // Clear error, let the empty state message show
        } else {
          setError(null);
        }
        
        usersCache.userData = paginatedItems;
        usersCache.page = currentPage;
        usersCache.totalRecords = computedTotal;
        usersCache.lastSearch = debouncedSearch;
        usersCache.lastStartDate = startDate;
        usersCache.lastEndDate = endDate;
        usersCache.firstLoad = false;
      } else {
        const errorMsg = response?.message || 'API returned an error';
        setError(errorMsg);
        usersCache.firstLoad = false;
      }
    } catch (err) {
      const errorMsg = err.message || 'Error loading user data. Please try again.';
      setError(errorMsg);
      setIsLoading(false);
      usersCache.firstLoad = false;
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
    }
  };

  // Load user data on mount and when page/debounced search/date filters change
  useEffect(() => {
    const isSearchChange = debouncedSearch !== (usersCache.lastSearch || '');
    const isDateChange = startDate !== (usersCache.lastStartDate || '') || endDate !== (usersCache.lastEndDate || '');
    const hasCache = usersCache.userData !== null && !usersCache.firstLoad && !isSearchChange && !isDateChange;
    
    if (hasCache && isInitialMountRef.current && currentPage === usersCache.page && debouncedSearch === (usersCache.lastSearch || '') && startDate === (usersCache.lastStartDate || '') && endDate === (usersCache.lastEndDate || '')) {
      setUserData(usersCache.userData);
      setCurrentPage(usersCache.page);
      setTotalRecords(usersCache.totalRecords);
      lastCallParamsRef.current = { page: currentPage, search: debouncedSearch, startDate, endDate };
      isInitialMountRef.current = false;
      return;
    }
    
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, startDate, endDate]);


  // Calculate pagination values
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRecords);

  const handleView = (item) => {
    setSelectedUser(item);
    setShowModal(true);
  };


  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden min-w-0">
        <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-y-auto no-scrollbar">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">Users Management</h1>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:justify-between gap-3 md:gap-4">
              {/* Search Input */}
              <div className="w-40 md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none focus:border-[#ECECEC] bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Loader or Table View */}
          {isLoading ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : userData.length === 0 && !error ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500 pointer-events-auto">
                No users found matching your criteria.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm min-w-0">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[920px] border-collapse">
                <thead className="bg-white border-b border-[#ECECEC]">
                  <tr>
                    {/* <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      User Image
                    </th> */}
                    <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider min-w-[140px]">
                      Name
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Email
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider min-w-[130px]">
                      Phone
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Registered At
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider min-w-[80px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#ECECEC]">
                  {error ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-6 text-center text-red-500">
                        {error}
                      </td>
                    </tr>
                  ) : (
                    userData.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-white/30 transition-colors"
                      >
                        {/* <td className="px-4 py-2.5 text-sm break-words">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-8 h-8 rounded-xl object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/32?text=No+Image';
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-xl bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">
                                {item.name?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          )}
                        </td> */}
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">
                          {item.name}
                        </td>
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">
                          {item.email}
                        </td>
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">
                          {item.phone || '-'}
                        </td>
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">
                          {formatRegisteredAt(item.createdAt)}
                        </td>
                        <td className="px-3 md:px-4 py-2.5 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-1 md:gap-2">
                            {/* <span
                              className={`px-2 py-1.5 text-xs font-semibold rounded-lg min-w-[84px] text-center h-[28px] flex items-center justify-center flex-shrink-0 ${
                                item.isActive
                                  ? 'bg-[#9FF7C9]/30 text-[#60BC7C]'
                                  : 'bg-[#FFF0F0] text-[#F76464]'
                              }`}
                            >
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span> */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(item);
                              }}
                              className="p-1.5 rounded-lg transition-colors hover:opacity-90 flex-shrink-0"
                              style={{ backgroundColor: '#F2E2F7' }}
                              title="View"
                            >
                              <HiEye className="w-4 h-4" style={{ color: '#58327E' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                              : 'text-gray-900 hover:bg-gray-100'
                          }`}
                          aria-current={page === currentPage ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      <UserModelView
        user={selectedUser}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default Users;
